<template>
  <div class="relative flex flex-col justify-center w-full">
    <form
      class="w-full space-y-8"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
      @keydown.esc.prevent="$emit('close')"
    >
      <h2
        @click="showTooltip = !showTooltip"
        class="text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
      >
        {{ item.questionDisplay }}
        <span v-if="item.required" class="text-ruby">*</span>
      </h2>
      <label for="" class="text-sm text-navada">{{ item.placeholder }} </label>
      <file-pond
        v-focus
        name="application-documents"
        ref="pond"
        :label-idle="t('drop')"
        :allow-multiple="false"
        accepted-file-types="image/jpeg, image/png"
        credits="{}"
        @init="handleFilePondInit"
      />

      <div
        v-if="selected"
        class="flex items-center justify-center w-full p-4 mt-6 mb-6 bg-white rounded-md shadow drop-shadow"
      >
        <img class="w-1/2" :src="selected" alt="" />
      </div>

      <!-- Desktop action -->
      <div
        class="flex-col justify-between hidden w-full pt-4 md:flex md:w-full md:flex-row"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <button
          v-if="!item.required"
          @click="$emit('skip', item, selected)"
          class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
        >
          <span class="text-black dark:text-white">{{ t("input.skip") }}</span>
          <SkipIcon />
        </button>
      </div>
      <!-- Mobile action -->
      <div class="flex flex-col w-full pt-4 space-y-4 md:hidden">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 w-full min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
          <button
            v-if="!item.required"
            @click="$emit('skip', item, selected)"
            class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white">{{
              t("input.skip")
            }}</span>
            <SkipIcon />
          </button>
        </div>
      </div>
    </form>
    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import vueFilePond, { setOptions } from "vue-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

import Spinner from "../Spinner";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";

import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";

const FilePond = vueFilePond(
  //FilePondPluginPdfPreview,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType
);

export default {
  name: "File",
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    application: {
      type: Object,
      required: true,
    },
  },
  components: {
    SkipIcon,
    ReturnIcon,
    Spinner,
    FilePond,
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const pond = ref(null);
    const answer = computed(() => props.item.answer);
    const selected = ref(answer.value ? answer.value.answer : "");

    const state = reactive({
      showTooltip: false,
      showError: false,
      isDeleting: false,
      error: "",
    });

    onMounted(() => {
      selected.value = answer.value ? answer.value.answer : "";
    });

    const next = () => {
      if (
        props.item.required &&
        (answer.value === null || answer.value.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }
      emit("next", props.item, selected.value, true);
    };

    const handleFilePondInit = () => {
      setOptions({
        server: {
          process: fDocProcess,
          revert: fDocDelete,
        },
      });
    };

    const deleteFile = async (key) => {
      if (!key || key.length === 0) {
        return;
      }

      try {
        const { data } = await axios.delete("/answers/files", {
          headers: {},
          data: {
            application: props.application,
            id: props.item.id,
            key: props.item.key,
            value: key,
          },
        });

        emit("answer", data);
      } catch (e) {}
    };

    const fDocProcess = async (
      fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort
    ) => {
      try {
        // Get signed URL from AWS
        const { data } = await axios.post("/assets/signed", {
          acl: "public-read",
          type: file.type,
        });
        const headers = data.headers;
        const options = {
          headers,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progress(
              percentCompleted,
              progressEvent.loaded,
              progressEvent.total
            );
          },
        };
        // Upload to AWS using signed url
        await axios.put(data.signedUrl, file, options);

        const params = {
          application: props.application,
          id: props.item.id,
          key: props.item.key,
          value: data.url,
          method: "create",
        };

        // Update answer with file key
        const res = await axios.put("/answers", params);

        // Update current files
        emit("answer", res.data);

        selected.value = data.url;

        load(data.url);
      } catch (err) {
        error(err);
      }

      // Should expose an abort method so the request can be cancelled
      return {
        abort: () => {
          // Let FilePond know the request has been cancelled
          abort();
        },
      };
    };

    const fDocDelete = async (uniqueFileId, load, error) => {
      /*await axios.delete('/applications/files', {
                                  headers: {},
                                  data: {
                                      id: item.value.id,
                                      key: uniqueFileId
                                  }
                              })*/

      load();
    };

    return {
      t,
      deleteFile,
      pond,
      selected,
      next,
      handleFilePondInit,
      ...toRefs(state),
    };
  },
};
</script>
